import { yupResolver } from '@hookform/resolvers/yup';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  useEditActivePoolsMutation,
  useGetPoolsQuery,
  useGetSeasonsQuery,
} from 'common/api/endpoints/harvestData';
import { handleError } from 'common/api/utils/handleError';
import { DataTable, DataTableProps } from 'common/components/DataTable';
import { MainContainerWrapper } from 'common/components/MainContainerWrapper/MainContainerWrapper';
import { MainTitle } from 'common/components/MainContainerWrapper/styles';
import { WizardDropdownOption } from 'common/components/WizardControls/WizardDropdown';
import { Season } from 'common/models/season';
import * as notifier from 'common/services/notification';
import {
  SeasonSelector,
  SelectedSeason,
} from 'features/navbar/components/SeasonSelector/SeasonSelector';
import {
  PoolTableRows,
  usePoolsTableData,
} from 'features/settings-view/hooks/usePoolsTableData';
import { FC, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { poolsSchema } from 'utils/schemas/settings';
import {
  formatDefaultPools,
  toEditActivePoolsDto,
} from '../RatesView/utils/functions';
import { DefaultSeasonForm } from './DefaultSeasonForm';

export type ActivePoolStatuses = Record<string, boolean>;

export const SeasonsView: FC = () => {
  const {
    data: seasons,
    isLoading: isLoadingSeasons,
    isFetching: isFetchingSeasons,
  } = useGetSeasonsQuery();
  const [editActivePools, { isLoading: isSubmitting }] =
    useEditActivePoolsMutation();

  const defaultSeason: WizardDropdownOption<Season> | null = useMemo(() => {
    const season = seasons?.find(ssn => ssn.isDefault);

    return season ? { label: `${season.year}`, value: season } : null;
  }, [seasons]);

  const { control: seasonControl, watch: seasonWatch } =
    useForm<SelectedSeason>({
      values: { season: defaultSeason },
    });
  const selectedSeason = seasonWatch('season');

  const {
    data: pools,
    error,
    isLoading: isLoadingPools,
    isFetching: isFetchingPools,
  } = useGetPoolsQuery(
    selectedSeason ? { seasonId: selectedSeason.value.id } : skipToken,
  );
  const { columns, data, isEditing, setIsEditing } = usePoolsTableData(
    pools || [],
    isSubmitting,
  );

  const methods = useForm<ActivePoolStatuses>({
    values: formatDefaultPools(pools || []),
    resolver: yupResolver(poolsSchema),
  });
  const {
    getFieldState,
    formState: { isDirty },
    reset,
  } = methods;

  const closeForm = () => {
    reset();
    setIsEditing(false);
  };

  const onSubmit = async (data: Record<string, boolean>): Promise<void> => {
    if (!isDirty) {
      // No values to save.
      closeForm();
      return;
    }

    const dto = toEditActivePoolsDto(getFieldState, data);

    try {
      await editActivePools(dto).unwrap();
      notifier.showSuccessMessage('Pools updated successfully.');
      closeForm();
    } catch (e) {
      handleError(e, 'Unable to edit pools.');
    }
  };

  const tableProps: DataTableProps<PoolTableRows, ActivePoolStatuses> = {
    title: 'Pools',
    toolBar: (
      <div style={{ marginLeft: '10px' }}>
        <SeasonSelector
          isLoading={isLoadingSeasons || isFetchingSeasons}
          seasons={seasons || []}
          control={seasonControl}
        />
      </div>
    ),
    columns,
    data,
    isLoading: isLoadingPools,
    isFetching: isFetchingPools,
    maxTableWidth: '900px',
    formProps: {
      methods,
      onSubmit,
      onCancel: closeForm,
      isEditing,
      setIsEditing,
      isDirty,
      isSubmitting,
    },
  };

  useEffect(() => {
    if (error) {
      handleError(error, 'Unable to fetch pools.');
    }
  }, [error]);

  return (
    <MainContainerWrapper maxWidth='900px'>
      <MainTitle style={{ alignSelf: 'center', marginBottom: '20px' }}>
        Season Settings
      </MainTitle>
      <DefaultSeasonForm
        defaultSeason={defaultSeason}
        seasons={seasons || []}
        isLoading={isLoadingSeasons || isFetchingSeasons}
      />
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <DataTable {...tableProps} />
      </div>
    </MainContainerWrapper>
  );
};
